// Het leespaneel (Verkennen ronde 4, B3/B4): de alinea achter een citaat,
// leesbaar voor een mens.
//
// Drie niveaus in één paneel: de alinea met de kop van de sectie en de zin
// gemarkeerd (standaard), de hele sectie, het rapport opengescrold op deze
// plek. Daaronder "wat andere bronnen hierover zeggen": de vijf
// dichtstbijzijnde alinea's uit andere documenten, op de embedding.
//
// Alinea's zijn op tekenpositie gesneden en beginnen soms midden in een zin;
// het paneel toont vanaf de eerste volle zin (beginVolleZin uit de API) en
// biedt de vorige alinea aan. Nooit een gok: staat er geen alinea, dan zegt
// het paneel dat en toont het citaat met de link.

import { useEffect, useRef, useState, type ReactNode } from "react";

import { BetaalmuurPil, WaaropPil, WiePil, type BewijsBron } from "./Bewijs";
import { EmptyReason } from "./Empty";
import { PanelHeader, SectionLabel } from "./ui";
import type { GraafAlineaResponse } from "../contracts";
import { api } from "../lib/api";
import { color, radius, size, weight } from "../tokens";

const UITGEVER_LABEL: Record<string, string> = {
  beleid: "beleid en toezicht", onderzoek: "onderzoek", arbeidsmarktdata: "arbeidsmarktdata",
  bedrijf: "bedrijfsleven", media: "vakmedia", onderwijs: "onderwijs", regionaal: "regionaal", gemengd: "gemengd",
};

/** Methode v3: staat het wie-veld erbij, dan zegt de pil wie het is en wijkt
 *  het uitgeverstype uit de tekstregel (anders stond er "vakmedia · vakblad"). */
function bronregel(a: { uitgever?: string | null; uitgeverType?: string | null; datum?: string | null; wie?: string | null }): string {
  return [a.uitgever, !a.wie && a.uitgeverType ? UITGEVER_LABEL[a.uitgeverType] ?? a.uitgeverType : null, a.datum]
    .filter(Boolean).join(" · ");
}

/** De wie-pil en waarop de bron het baseert. Niets zonder de v3-velden. */
function BewijsPillen({ bron }: { bron: BewijsBron }) {
  if (!bron.wie && !bron.onderbouwingLabel && !bron.onderbouwing && !bron.achterBetaalmuur) return null;
  return (
    <span data-bewijspillen style={{ display: "inline-flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
      <WiePil bron={bron} />
      <WaaropPil bron={bron} />
      <BetaalmuurPil bron={bron} />
    </span>
  );
}

/** De opgeslagen tekst is markdown uit de parse-laag: links als [tekst](url),
 *  soms met een verdwaalde "(link naar andere website)]". Voor de lezer: de
 *  linktekst zonder de haken en adressen. */
export function opschonen(t: string): string {
  return t
    .replace(/\[([^\]]*)\]\((https?:[^)\s]+)\)/g, "$1")
    .replace(/\(link naar andere website\)\]?\s*\(?https?:[^)\s]*\)?/g, "")
    .replace(/\]\((https?:[^)\s]+)\)/g, "")
    .replace(/(^|\n)#{1,6}\s+/g, "$1")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n");
}

/** De tekst met het citaat gemarkeerd (op genormaliseerde vergelijking). */
function Gemarkeerd({ tekst: ruw, citaat }: { tekst: string; citaat?: string | null }): ReactNode {
  const tekst = opschonen(ruw);
  if (!citaat) return tekst;
  const norm = (t: string) => t.toLowerCase().replace(/[^a-z0-9]+/g, "");
  const doel = norm(citaat).slice(0, 60);
  if (doel.length < 20) return tekst;
  // zoek de plek in de ruwe tekst door genormaliseerde posities te volgen
  let genorm = "";
  const posities: number[] = [];
  for (let i = 0; i < tekst.length; i++) {
    const c = tekst[i]!.toLowerCase();
    if (/[a-z0-9]/.test(c)) { genorm += c; posities.push(i); }
  }
  const start = genorm.indexOf(doel);
  if (start < 0) return tekst;
  const van = posities[start]!;
  // einde: tot het einde van de zin na het citaat
  let tot = posities[Math.min(posities.length - 1, start + norm(citaat).length - 1)]! + 1;
  const zinEinde = tekst.slice(tot).search(/[.!?](\s|$)/);
  if (zinEinde >= 0 && zinEinde < 80) tot += zinEinde + 1;
  return (
    <>
      {tekst.slice(0, van)}
      <mark style={{ background: "#fef3c7", color: color.ink, padding: "0 2px", borderRadius: 3 }}>{tekst.slice(van, tot)}</mark>
      {tekst.slice(tot)}
    </>
  );
}

export function LeesPaneel({
  alineaId,
  citaat,
  onClose,
  onOpenAlinea,
  onOpenBron,
}: {
  alineaId: string;
  /** de zin die het verband draagt; wordt gemarkeerd */
  citaat?: string | null;
  onClose: () => void;
  /** een verwante alinea openen (vervangt deze) */
  onOpenAlinea: (id: string, citaat?: string | null) => void;
  /** het bronpaneel openen (alles wat uit dit rapport kwam) */
  onOpenBron?: (documentId: string) => void;
}) {
  const [niveau, setNiveau] = useState<"alinea" | "sectie" | "rapport">("alinea");
  const [data, setData] = useState<GraafAlineaResponse | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [toonVorige, setToonVorige] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let levend = true;
    setData(null); setFout(null); setToonVorige(false);
    api.graafAlinea(alineaId, niveau)
      .then((d) => levend && setData(d))
      .catch((e) => levend && setFout(String(e)));
    return () => { levend = false; };
  }, [alineaId, niveau]);

  useEffect(() => {
    if (niveau === "rapport" && data?.rapport && scroller.current) {
      const el = scroller.current.querySelector<HTMLElement>(`[data-alinea="${data.id}"]`);
      el?.scrollIntoView({ block: "center" });
    }
  }, [niveau, data]);

  const knop = (aan: boolean) => ({
    cursor: "pointer", fontSize: size.label, padding: "4px 10px", borderRadius: radius.input,
    border: `1px solid ${aan ? color.ink : color.border}`, background: aan ? color.ink : color.surface,
    color: aan ? color.surface : color.body, fontFamily: "inherit",
  });

  return (
    <div ref={scroller} style={{ height: "100%", overflow: "auto" }}>
      <PanelHeader onClose={onClose}>
        <span style={{ fontSize: size.label, color: color.faint, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          {data ? bronregel(data) : "bron"}
        </span>
        {data && <BewijsPillen bron={data} />}
        <span style={{ fontSize: size.body, fontWeight: weight.medium, color: color.ink }}>
          {data?.titel ?? "Alinea"}
        </span>
      </PanelHeader>
      <div style={{ padding: "0 24px 24px" }}>
        {fout && <EmptyReason reason={`alinea niet te laden: ${fout}`} />}
        {!data && !fout && <p style={{ color: color.faint, fontSize: size.meta }}>alinea laden…</p>}
        {data && (
          <>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "0 0 14px" }}>
              <button type="button" style={knop(niveau === "alinea")} onClick={() => setNiveau("alinea")}>Alinea</button>
              <button type="button" style={knop(niveau === "sectie")} onClick={() => setNiveau("sectie")}>Hele sectie</button>
              <button type="button" style={knop(niveau === "rapport")} onClick={() => setNiveau("rapport")}>Rapport op deze plek</button>
              {data.url && (
                <a href={data.url} target="_blank" rel="noreferrer" style={{ ...knop(false), textDecoration: "none", marginLeft: "auto" }}>
                  Origineel ↗
                </a>
              )}
            </div>

            {niveau === "alinea" && (
              <>
                <SectionLabel>{data.sectieKop ? data.sectieKop : "zonder sectiekop"} · alinea {data.plek.nummer} van {data.plek.van}</SectionLabel>
                {data.beginVolleZin > 0 && !toonVorige && (
                  <button type="button" onClick={() => setToonVorige(true)} style={{ ...knop(false), marginBottom: 8 }}>
                    … toon het begin van de zin
                  </button>
                )}
                <p style={{ fontSize: size.body, lineHeight: 1.65, color: color.body, margin: 0, whiteSpace: "pre-wrap" }}>
                  {toonVorige || data.beginVolleZin === 0
                    ? <Gemarkeerd tekst={data.tekst} citaat={citaat} />
                    : <Gemarkeerd tekst={data.tekst.slice(data.beginVolleZin)} citaat={citaat} />}
                </p>
                <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                  {data.vorigeId && <button type="button" style={knop(false)} onClick={() => onOpenAlinea(data.vorigeId!)}>← vorige alinea</button>}
                  {data.volgendeId && <button type="button" style={knop(false)} onClick={() => onOpenAlinea(data.volgendeId!)}>volgende alinea →</button>}
                </div>
              </>
            )}

            {niveau === "sectie" && data.sectie?.alineas && (
              <>
                <SectionLabel>{data.sectie.titel ?? "sectie"} · {data.sectie.alineas.length} alinea's</SectionLabel>
                {data.sectie.alineas.map((a) => (
                  <p key={a.id} data-alinea={a.id} style={{ fontSize: size.body, lineHeight: 1.65, color: a.id === data.id ? color.ink : color.body, margin: "0 0 12px", whiteSpace: "pre-wrap", borderLeft: a.id === data.id ? `3px solid #2563eb` : "3px solid transparent", paddingLeft: 10 }}>
                    {a.id === data.id ? <Gemarkeerd tekst={a.tekst} citaat={citaat} /> : opschonen(a.tekst)}
                  </p>
                ))}
              </>
            )}

            {niveau === "rapport" && data.rapport?.alineas && (
              <>
                <SectionLabel>het hele rapport · {data.rapport.alineas.length} alinea's</SectionLabel>
                {data.rapport.alineas.map((a, i) => {
                  const kopNieuw = a.sectieKop && a.sectieKop !== data.rapport?.alineas?.[i - 1]?.sectieKop;
                  return (
                    <div key={a.id} data-alinea={a.id}>
                      {kopNieuw && <h4 style={{ fontSize: size.body, fontWeight: weight.medium, color: color.ink, margin: "18px 0 6px" }}>{a.sectieKop}</h4>}
                      <p style={{ fontSize: size.meta, lineHeight: 1.6, color: a.id === data.id ? color.ink : color.muted, margin: "0 0 10px", whiteSpace: "pre-wrap", borderLeft: a.id === data.id ? `3px solid #2563eb` : "3px solid transparent", paddingLeft: 10 }}>
                        {a.id === data.id ? <Gemarkeerd tekst={a.tekst} citaat={citaat} /> : opschonen(a.tekst)}
                      </p>
                    </div>
                  );
                })}
              </>
            )}

            {niveau === "alinea" && data.verwant.length > 0 && (
              <div style={{ marginTop: 22 }}>
                <SectionLabel>Wat andere bronnen hierover zeggen</SectionLabel>
                {data.verwant.map((v) => (
                  <button
                    key={v.alineaId}
                    type="button"
                    onClick={() => onOpenAlinea(v.alineaId)}
                    style={{
                      display: "block", width: "100%", textAlign: "left", cursor: "pointer", fontFamily: "inherit",
                      background: color.surfaceQuote, border: `1px solid ${color.lineFaint}`, borderRadius: radius.quote,
                      padding: "9px 12px", marginBottom: 6,
                    }}
                  >
                    <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                      <span style={{ fontSize: size.meta, fontWeight: weight.medium, color: color.ink, flex: 1 }}>{v.titel ?? v.uitgever ?? "bron"}</span>
                      <span style={{ fontSize: size.label, color: color.faint, flexShrink: 0 }}>{Math.round(v.gelijkenis * 100)}% gelijkenis</span>
                    </div>
                    <div style={{ fontSize: size.label, color: color.faint, margin: "2px 0 4px" }}>{bronregel(v)}{v.sectieKop ? ` · ${v.sectieKop}` : ""}</div>
                    <div style={{ margin: "0 0 4px" }}><BewijsPillen bron={v} /></div>
                    <div style={{ fontSize: size.meta, color: color.muted, lineHeight: 1.5 }}>{opschonen(v.begin ?? "")}…</div>
                  </button>
                ))}
              </div>
            )}

            {onOpenBron && (
              <div style={{ marginTop: 18 }}>
                <button type="button" style={knop(false)} onClick={() => onOpenBron(data.documentId)}>
                  Alles wat uit dit rapport kwam →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
